import { useInView } from "../../hooks/useInView";
import { useCmsLink } from "../../hooks/useCmsLink";
import { useHomePage } from "../../context/HomePageContext";

import { resolveRecentProject } from "../../lib/homePageContent";
import { useEffect, useMemo, useState } from "react";
import { getProject } from "../../api/projectApi";
import { useLanguage } from "../../context/LanguageContext";

interface Project {
  id: number;
  projectName: string;
  image: string | null;
  description: string;
  status: string;
}

export default function RecentProjectSection() {
  const goTo = useCmsLink();
  const { data } = useHomePage();
  const { language } = useLanguage();
  const content = useMemo(
    () => resolveRecentProject(data?.recent_project, language),
    [data, language],
  );
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);

  const { ref, inView } = useInView<HTMLDivElement>();

  const fetchProject = async () => {
    try {
      setLoading(true);

      const response = await getProject({
        page: 1,
        limit: 4,
      });

      setProjects(response.data.projectStructureList);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, []);

  return (
    <section
      className="
        w-full


        px-6
        py-20

        lg:px-20
        lg:py-24
      ">
      <div
        ref={ref}
        className="
          w-full
          max-w-[1327px]

          mx-auto
        ">
        {/* TITLE */}
        <div
          className={`
            flex
            justify-center

            mb-14

            reveal-up
            ${inView ? "is-visible" : ""}
          `}>
          <h2
            className="
              text-[#324A5A]
              text-center

              text-[38px]
              font-bold

              lg:text-[52px]
            ">
            {content.title}
          </h2>
        </div>

        {/* PROJECT GRID */}
        <div
          className="
            grid
            grid-cols-1

            gap-6

            sm:grid-cols-2

            lg:grid-cols-4
          ">
          {projects.map((project, index) => (
            <div
              key={index}
              style={{
                transitionDelay: inView ? `${index * 110}ms` : "0ms",
              }}
              className={`
                group

                reveal-up
                ${inView ? "is-visible" : ""}

                relative

                overflow-hidden

                rounded-[12px]

                cursor-pointer
              `}>
              {/* IMAGE */}

              {project.image ? (
                <img
                  src={project.image}
                  alt="project"
                  className="
                  w-full

                  h-[260px]

                  object-cover

                  transition-transform
                  duration-500

                  group-hover:scale-105
                "
                />
              ) : (
                <div
                  className="
                    absolute
                    inset-0

                    flex
                    items-center
                    justify-center

                    bg-gray-200
                    text-gray-400
                  ">
                  No Image
                </div>
              )}

              {/* OVERLAY */}
              <div
                className="
                  absolute
                  inset-0

                  bg-black/0

                  transition-all
                  duration-300

                  group-hover:bg-black/10
                "
              />
            </div>
          ))}
        </div>

        {/* BUTTON */}
        <div
          style={{ transitionDelay: inView ? "480ms" : "0ms" }}
          className={`
            flex
            justify-center

            mt-16

            reveal-up
            ${inView ? "is-visible" : ""}
          `}>
          {content.buttonLabel && (
            <button
              onClick={() => goTo(content.buttonLink)}
              className="
              w-[225px]
              h-[58px]

              rounded-full

              bg-[#3DA4A8]

              text-white

              text-[20px]
              font-semibold

              transition-all
              duration-300

              hover:bg-[#2F8C90]
              hover:shadow-xl
              hover:-translate-y-1
            ">
              {content.buttonLabel}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
